/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Atom, WritableAtom } from 'jotai/vanilla'
import type { Getter } from 'jotai'
import {
  atom,
  useAtom as jotaiUseAtom,
  useAtomValue as jotaiUseAtomValue,
  useSetAtom as jotaiUseSetAtom,
} from 'jotai'

// ---- helpers ---------------------------------------------------------------
// raw value from an Atom
type AtomValue<A extends Atom<any>> = A extends Atom<infer V> ? V : never
// unwrap Promise/Thenable for display/use in React
type Resolved<A extends Atom<any>> = Awaited<AtomValue<A>>

// update/result from a WritableAtom
type AtomUpdate<A extends WritableAtom<any, any, any>> =
  A extends WritableAtom<any, infer U, any> ? U : never
type AtomResult<A extends WritableAtom<any, any, any>> =
  A extends WritableAtom<any, any, infer R> ? R : never

// setter compatible with v1 (rest args) and v2 (single arg)
type SetterFor<A extends WritableAtom<any, any, any>> =
  AtomUpdate<A> extends any[]
    ? (...args: AtomUpdate<A>) => AtomResult<A>
    : (update: AtomUpdate<A>) => AtomResult<A>

// ---- wrappers with overloads ----------------------------------------------
// Writable overload
export function useAtom<A extends WritableAtom<any, any, any>>(
  a: A,
): [Resolved<A>, SetterFor<A>]
// Readonly overload
export function useAtom<A extends Atom<any>>(a: A): [Resolved<A>, never]
export function useAtom(a: any) {
  return jotaiUseAtom(a)
}

export function useAtomValue<A extends Atom<any>>(a: A): Resolved<A>
export function useAtomValue(a: any) {
  return jotaiUseAtomValue(a)
}

export function useSetAtom<A extends WritableAtom<any, any, any>>(
  a: A,
): SetterFor<A>
export function useSetAtom(a: any) {
  return jotaiUseSetAtom(a)
}

export { atom }
export type { Atom, WritableAtom, Getter }
